/**
 * GiftBell — the header bell, carrying TWO INDEPENDENT INDICATORS (Gifting Batch 5).
 *
 * Batch 4a made the bell FLIP: when a gift was pending it became a 🎁 that routed to
 * the inbox, hid the unread count, and left the feed unreachable from the header.
 * Two unrelated signals were fighting over one slot, and the gift always won.
 *
 * They no longer compete:
 *   - the red unread DOT (notifications_unread_count) — ALWAYS the far-right slot,
 *     and identical to the generic bell's dot;
 *   - a 🎁 indicator (pending inbound gifts, GET /api/gift/) — sits to its LEFT when
 *     both are showing, and takes the right slot when it is alone.
 *
 * TAP TARGETS: the bell itself ALWAYS opens the feed (/notifications) — it is never
 * hijacked, so the feed can never become unreachable. The 🎁 indicator is its own
 * button and opens the inbox (/perfil/recebidos). Two signals, two destinations.
 *
 * DARK-LAUNCH CONTRACT: unchanged from 4a in substance, but the gate is now server-driven.
 * This component is rendered ONLY from the `giftingGate === "on" && loggedIn` branch in
 * Header — strictly "on", never on an undetermined gate, precisely because the useEffect
 * below fires GET /api/gift/ unconditionally on mount. The gate ANDs in the build-time flag
 * FIRST (see utils/giftingFlag), which is false in prod — so flag-off it never mounts, its
 * useEffect never runs, and no GET /api/gift/ fires. Prod keeps rendering
 * Header's untouched else-branch bell, which is character-identical to this one's
 * no-gift state. (Runtime-dormant, not dead-code-eliminated — like all gifting code
 * here, it still ships in the bundle.) Because it only mounts for a logged-in user
 * with gifting ON, it fetches unconditionally on mount.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { Tooltip } from "@mui/joy";
import { useTranslation } from "react-i18next";
import BellIcon from "../../Icon/BellIcon";
import { useReceivedGifts } from "../../../api/hooks/gift";
import styles from "./index.module.css";

const GiftBell: React.FC<{ notificationsCount: number }> = ({
  notificationsCount,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { gifts, fetchReceivedGifts } = useReceivedGifts();

  // Once per session — a badge signal, not a live feed. fetchReceivedGifts is a
  // stable useCallback, so this runs a single time on mount.
  useEffect(() => {
    fetchReceivedGifts();
  }, [fetchReceivedGifts]);

  const hasGift = gifts.length > 0;
  // "Is the red dot on screen" — which is what decides whether the gift indicator has
  // to step left to make room for it.
  const hasUnread = notificationsCount > 0;

  return (
    <div className={styles.bellStack}>
      {/* Always the feed. The gift no longer steals this. */}
      <button
        aria-label={t("nav.notifications") || "Notificações"}
        className={styles.iconBtn}
        onClick={() => navigate("/notifications")}
      >
        <span className={styles.bellIconWrap}>
          <BellIcon size="24px" color="#9AA9B3" />
          {hasUnread && <span className={styles.unreadDot} />}
        </span>
      </button>

      {hasGift && (
        <Tooltip title={t("gift.bellTooltip")} placement="bottom" size="sm">
          <button
            aria-label={t("gift.bellTooltip")}
            className={`${styles.giftIndicator} ${
              hasUnread ? styles.giftIndicatorShifted : ""
            }`}
            onClick={() => navigate("/perfil/recebidos")}
          >
            <span className={styles.giftIndicatorGlyph} aria-hidden="true">
              🎁
            </span>
          </button>
        </Tooltip>
      )}
    </div>
  );
};

export default GiftBell;
